// Login page — handles credentials, MFA token, and MFA setup flow.
// MFA setup deadlock fix: when API returns mfa_setup_required, we call
// /auth/mfa-bootstrap (pre-auth) to generate a TOTP secret + QR code,
// display it so the user can scan with their authenticator, then verify
// with /auth/mfa-bootstrap-verify to complete setup and receive tokens.

import { useState, type JSX, type FormEvent } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth, MfaRequiredError, MfaSetupRequiredError } from "../hooks/useAuth.js";
import { ApiError } from "../lib/api.js";
import {
  LoginHeader, CredentialsStep, MfaTokenStep, MfaSetupLoadingStep,
  MfaSetupScanStep, MfaSetupVerifyStep, SaveRecoveryCodesStep,
} from "./LoginPage.components.js";

type Step = "credentials" | "mfa_token" | "mfa_setup_loading" | "mfa_setup" | "mfa_setup_verify" | "save_recovery_codes";

// Honours VITE_API_BASE in prod (split-origin: app.hcmcrm.com → api.hcmcrm.com).
// Empty fallback keeps the dev Vite proxy working.
const API_BASE = (import.meta.env.VITE_API_BASE ?? "") + "/hcm/api/v1";
const CONNECTION_ERR = "Unable to connect. Check your internet connection.";
const DASHBOARD_PATH = "/dashboard";
const CHANGE_PASSWORD_PATH = "/change-password";

interface BootstrapResponse {
  qr_uri: string;
  secret: string;
  setup_token: string;
}

interface BootstrapVerifyResponse {
  access_token: string;
  refresh_token: string;
  user: { id: string; email: string; role: string; tenant_id: string; must_change_password?: boolean };
  // Returned ONCE on verify so the user can save them. Sha256-hashed in the DB.
  recovery_codes: string[];
}

function resolveErrorMessage(err: unknown): string {
  if (err instanceof MfaRequiredError || err instanceof MfaSetupRequiredError) return "";
  if (err instanceof ApiError) {
    if (err.status === 401) return "Invalid email or password.";
    if (err.status === 429) return "Too many attempts. Please wait a moment.";
    return "Something went wrong. Please try again.";
  }
  return CONNECTION_ERR;
}

// ── Main LoginPage ──────────────────────────────────────────────────────────

// eslint-disable-next-line max-lines-per-function -- coherent React component / single-purpose hook; splitting would shuffle lines without semantic gain. Tracked under TASK-N19 Phase B.
export function LoginPage(): JSX.Element {
  const { user, login, isLoading, refreshFromStorage } = useAuth();
  const navigate = useNavigate();
  if (user) return <Navigate to={DASHBOARD_PATH} replace />;

  const [step, setStep]             = useState<Step>("credentials");
  const [email, setEmail]           = useState("");
  const [password, setPass]         = useState("");
  const [totp, setTotp]             = useState("");
  const [error, setError]           = useState<string | null>(null);
  const [setupLoading, setSetupLoading] = useState(false);

  // MFA bootstrap state
  const [qrUri, setQrUri]           = useState("");
  const [secret, setSecret]         = useState("");
  const [setupToken, setSetupToken] = useState("");

  // Post-verify state. The server has already issued tokens, but we hold them
  // in component state until the user confirms they've saved the recovery
  // codes — only then do we persist + refresh the AuthProvider. Activating
  // auth earlier triggers the `if (user) return <Navigate>` early-return at
  // the top of this component, which changes the hook count between renders
  // (React error #300).
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [postSaveTarget, setPostSaveTarget] = useState<string>(DASHBOARD_PATH);
  const [pendingAccessToken, setPendingAccessToken] = useState<string | null>(null);
  const [pendingUser, setPendingUser] = useState<BootstrapVerifyResponse["user"] | null>(null);

  // ── Step 1: credentials ──────────────────────────────────────────────────

  async function handleCredentials(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    try {
      await login(email.trim(), password);
      // Forced password change after invite or admin reset.
      const cached = sessionStorage.getItem("hcm_user");
      const u = cached ? (JSON.parse(cached) as { must_change_password?: boolean }) : null;
      navigate(u?.must_change_password ? CHANGE_PASSWORD_PATH : DASHBOARD_PATH, { replace: true });
    } catch (err) {
      if (err instanceof MfaRequiredError) { setStep("mfa_token"); return; }
      if (err instanceof MfaSetupRequiredError) {
        setStep("mfa_setup_loading");
        await startMfaBootstrap();
        return;
      }
      setError(resolveErrorMessage(err));
    }
  }

  // ── Step 2 (setup path): call pre-auth bootstrap endpoint ────────────────

  async function startMfaBootstrap(): Promise<void> {
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/auth/mfa-bootstrap`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { error?: string };
        setError(body.error ?? "Failed to start MFA setup. Please try again.");
        setStep("credentials");
        return;
      }
      const data = await res.json() as BootstrapResponse;
      setQrUri(data.qr_uri);
      setSecret(data.secret);
      setSetupToken(data.setup_token);
      setStep("mfa_setup");
    } catch {
      setError(CONNECTION_ERR);
      setStep("credentials");
    }
  }

  // ── Step 3 (setup path): user scanned QR, proceed to verify ─────────────

  function handleScanConfirmed(): void {
    setTotp("");
    setError(null);
    setStep("mfa_setup_verify");
  }

  // ── Step 4 (setup path): verify TOTP against the bootstrap token ─────────

  async function handleMfaSetupVerify(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setSetupLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/mfa-bootstrap-verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ setup_token: setupToken, totp_token: totp }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { error?: string };
        setError(body.error ?? "Invalid code. Please try again.");
        setSetupLoading(false);
        return;
      }
      const data = await res.json() as BootstrapVerifyResponse;
      // Hold the token + user in component state so we can show the recovery
      // codes ONCE before activating the session. Persisting now would make
      // `user` truthy on the next render, hitting the early-return guard at
      // the top of LoginPage and crashing with hook-count mismatch
      // (React error #300).
      setPendingAccessToken(data.access_token);
      setPendingUser(data.user);
      setRecoveryCodes(data.recovery_codes ?? []);
      setPostSaveTarget(data.user.must_change_password ? CHANGE_PASSWORD_PATH : DASHBOARD_PATH);
      setStep("save_recovery_codes");
    } catch {
      setError(CONNECTION_ERR);
    } finally {
      setSetupLoading(false);
    }
  }

  // ── Step (existing MFA token path) ───────────────────────────────────────

  async function handleMfaToken(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    try {
      await login(email.trim(), password, totp);
      const cached = sessionStorage.getItem("hcm_user");
      const u = cached ? (JSON.parse(cached) as { must_change_password?: boolean }) : null;
      navigate(u?.must_change_password ? CHANGE_PASSWORD_PATH : DASHBOARD_PATH, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError && err.status === 401 ? "Invalid code. Try again." : resolveErrorMessage(err));
    }
  }

  function resetToCredentials(): void { setStep("credentials"); setTotp(""); setError(null); }
  function backToScanStep(): void { setStep("mfa_setup"); setTotp(""); setError(null); }

  // Called after the user confirms they've saved their recovery codes.
  // Activates the session that mfa-bootstrap-verify already issued, then
  // routes to change-password or dashboard depending on the invite flag.
  async function commitPendingSession(): Promise<void> {
    if (!pendingAccessToken || !pendingUser) return;
    const { setAccessToken } = await import("../lib/api.js");
    setAccessToken(pendingAccessToken);
    sessionStorage.setItem("hcm_user", JSON.stringify(pendingUser));
    refreshFromStorage();
    navigate(postSaveTarget, { replace: true });
  }

  const isSubmitting = isLoading || setupLoading;

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <LoginHeader />

        {step === "credentials" && (
          <CredentialsStep email={email} setEmail={setEmail} password={password} setPass={setPass}
            error={error} isLoading={isLoading} onSubmit={(e) => { void handleCredentials(e); }} />
        )}

        {step === "mfa_token" && (
          <MfaTokenStep totp={totp} setTotp={setTotp} error={error} isLoading={isLoading}
            onSubmit={(e) => { void handleMfaToken(e); }} onBack={resetToCredentials} />
        )}

        {step === "mfa_setup_loading" && (
          <MfaSetupLoadingStep />
        )}

        {step === "mfa_setup" && (
          <MfaSetupScanStep
            qrUri={qrUri}
            secret={secret}
            error={error}
            onContinue={handleScanConfirmed}
            onBack={resetToCredentials}
          />
        )}

        {step === "mfa_setup_verify" && (
          <MfaSetupVerifyStep
            totp={totp} setTotp={setTotp}
            error={error} isLoading={isSubmitting}
            onSubmit={(e) => { void handleMfaSetupVerify(e); }}
            onBack={backToScanStep}
          />
        )}

        {step === "save_recovery_codes" && (
          <SaveRecoveryCodesStep
            codes={recoveryCodes}
            email={email.trim()}
            onContinue={() => { void commitPendingSession(); }}
          />
        )}
      </div>
    </div>
  );
}
